// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MathUtils } from '@shared/utils/MathUtils';
export class Asset {
    constructor(dataCache, assetMessage) {
        /// <summary>
        /// Рассчетное поле на основании мин чейнджа
        /// </summary>
        this.Point = 2; // int
        this.InstrumentId = -1; // int
        this.RouteId = -1; // int
        this.CreditForSell = null; // double?
        this.LiquidityRate = null; // double?
        this.InstrumentTradableID = -1;
        this._instrument = null;
        this._sellExchanges = null;
        this._insForTading = null;
        this.floatDecimalFormatStatic = null;
        this.DataCache = dataCache;
        this.FillByMessage(assetMessage);
        this.CreateFormatter();
    }
    toJSON() {
        return {
            Id: this.Id,
            Type: this.Type,
            Name: this.Name
        };
    }
    FillByMessage(message) {
        this.Id = message.Id;
        this.Name = message.Name;
        this.Type = message.Type;
        this.Description = message.Description;
        this.MinChange = message.MinChange;
        if (this.MinChange > 0) {
            this.Point = MathUtils.GetDecimalPlaces(this.MinChange, 8);
        }
        if (message.InstrumentId !== null) {
            this.InstrumentId = message.InstrumentId;
        }
        if (message.RouteId !== null) {
            this.RouteId = message.RouteId;
        }
        this.CreditForSell = message.CreditForSell;
        this.LiquidityRate = message.LiquidityRate;
        this.InstrumentTradableID = message.InstrumentTradableID;
    }
    toString() {
        return this.Name;
    }
    formatPrice(value, noCurrency = false) {
        if (isNaN(value)) {
            value = 0;
        }
        value = MathUtils.TruncateDouble(value, this.Point); // для предотвращения округления в форматере
        return this.formatString(value, noCurrency);
    }
    roundFormatPrice(value, noCurrency = false) {
        if (isNaN(value)) {
            value = 0;
        }
        value = MathUtils.RoundDouble(value, this.Point);
        return this.formatString(value, noCurrency);
    }
    formatString(value, noCurrency = false) {
        if (noCurrency) {
            return this.floatDecimalFormatStatic.format(value);
        }
        return this.floatDecimalFormatStatic.format(value) + ' ' + this.Name;
    }
    get Instrument() {
        if (this._instrument && !this.instrPr) {
            return this._instrument;
        }
        if (this.instrPr) {
            return null;
        }
        this.instrPr = this.GetInstrumentAsync().then(function (instrument) {
            this._instrument = instrument;
            this.instrPr = null;
        }.bind(this));
        return null;
    }
    GetInstrumentAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.InstrumentTradableID !== -1 && this.RouteId !== -1) {
                return yield this.DataCache.getInstrumentByInstrumentTradableID_NFL(this.InstrumentTradableID, this.RouteId).then(function (instrument) {
                    if (!instrument) {
                        return null;
                    }
                    if (instrument.TradableRoutes.indexOf(instrument.Route) === -1 &&
                        instrument.TradableRoutes.length) {
                        return this.DataCache.getInstrumentByInstrumentTradableID_NFL(this.InstrumentTradableID, instrument.TradableRoutes[0]);
                    }
                    return instrument;
                }.bind(this));
                // var list = BaseApplication.App.MultiDataCache.Instruments.GetNonFixedInstrumentsByTradableID(TradableId);
                // if (list.Count(x => x.RouteId == this.RouteId) != 0 && list.Count == 2) // кейс, когда схлопываются роуты
                //     return list.Where(x => x.RouteId != this.RouteId).FirstOrDefault();
            }
            return yield Promise.resolve(null);
        });
    }
    get SellExchanges() {
        if (this._sellExchanges) {
            return this._sellExchanges;
        }
        this.UpdateSellExchanges();
        return null;
    }
    GetPreferedInstrumentInterriorIdByExchange(exchange) {
        if (!this._insForTading) {
            return null;
        }
        const arr = this._insForTading[exchange];
        if (!arr) {
            return null;
        }
        return arr[0] || null;
    }
    UpdateSellExchanges() {
        return this.DataCache.GetNonFixedInstrumentListByAssetName(this.Name)
            .then(function (instruments) {
            const sellExchangesObj = {};
            const insForTading = {};
            for (let i = 0; i < instruments.length; i++) {
                const ins = instruments[i];
                sellExchangesObj[ins.TradingExchange] = true;
                if (!insForTading[ins.TradingExchange]) {
                    insForTading[ins.TradingExchange] = [];
                }
                insForTading[ins.TradingExchange].push(ins.GetInteriorID());
            }
            this._insForTading = insForTading;
            const sellExchangesArray = Object.keys(sellExchangesObj);
            sellExchangesArray.sort();
            if (!this._instrument) {
                return this.GetInstrumentAsync().then(function (instrument) {
                    this._instrument = instrument;
                    return sellExchangesArray;
                }.bind(this));
            }
            return sellExchangesArray;
        }.bind(this))
            .then(function (sellExchangesArray) {
            if (!this._instrument) {
                return sellExchangesArray;
            }
            const index = sellExchangesArray.indexOf(this._instrument.TradingExchange);
            sellExchangesArray.splice(index, 1);
            sellExchangesArray.unshift(this._instrument.TradingExchange);
            this._sellExchanges = sellExchangesArray;
            return this._sellExchanges;
        }.bind(this));
    }
    CreateFormatter() {
        // nicky
        // при форматировании цены применялись точности из ассета (Point)
        // и при параллельных запросах очень часто возникал баз из-за
        // использования несинхронизированного доступа к форматтеру из инструмента.
        //
        // чтобы не ухудшать перфоманс локом, вынес каждому ассету личный форматтер
        //
        this.floatDecimalFormatStatic = new Intl.NumberFormat(undefined, {
            minimumFractionDigits: this.Point,
            maximumFractionDigits: this.Point
        });
    }
    IsEqualAsset(assetToCheckEqualWith) {
        return Asset.IsEqualAsset(this, assetToCheckEqualWith);
    }
    static IsEqualAsset(a1, a2) {
        if (a1 == null || a2 == null) {
            return false;
        }
        return a1.Id === a2.Id;
    }
}
export var AssetType;
(function (AssetType) {
    AssetType[AssetType["CURRENCY"] = 0] = "CURRENCY";
    AssetType[AssetType["SHARES"] = 1] = "SHARES";
    AssetType[AssetType["COMMODITIES"] = 2] = "COMMODITIES";
    AssetType[AssetType["CRYPTO_CCY"] = 3] = "CRYPTO_CCY";
})(AssetType || (AssetType = {}));
